import { useState } from 'react'
import CartaoErro from '../../componentes/CartaoErro'
import { formatarMoeda } from '../../lib/formatos'
import { baixarArquivo, compartilharArquivo } from '../../pedidos/compartilhar'
import type { DadosRelatorio } from '../../comissoes/PdfConferencia'
import { sinal, rotuloDoAjuste, valorComSinal } from '../../comissoes/sinalDoAjuste'

// Spec 2026-10-04 (Baixa de Comissao) §5: o rodape fixo. Fixo e sempre visivel
// porque ela CONFERE aritmetica contra o extrato da fabrica: ver o resultado
// enquanto decide e o que desfaz ambiguidade. Na aba A receber traz os numeros
// e o "Dar baixa"; nas outras, so o "Gerar relatorio".
export default function RodapeDaBaixa({
  refDoRodape, comBaixa, conta, quantidade, foraDoFiltro, fornecedora,
  confirmando, setConfirmando, aoDarBaixa, erroBaixa, gerarDados, pagoEDiferenca, aoAbrirTotal,
}: {
  refDoRodape: (elemento: HTMLDivElement | null) => void
  comBaixa: boolean
  conta: { selecionados: number; ajuste: number; total: number }
  quantidade: number
  foraDoFiltro: number
  fornecedora: string
  confirmando: boolean
  setConfirmando: (sim: boolean) => void
  aoDarBaixa: () => Promise<void>
  erroBaixa: string | null
  gerarDados: () => DadosRelatorio
  // Spec 2026-10-05: com total pago digitado, o rodape mostra o pago e a
  // diferenca do mes; tocar no total abre a janela.
  pagoEDiferenca: { pago: number; diferenca: number } | null
  aoAbrirTotal: () => void
}) {
  const [menuAberto, setMenuAberto] = useState(false)
  const [gerando, setGerando] = useState(false)
  const [erroRelatorio, setErroRelatorio] = useState<string | null>(null)

  // A biblioteca de PDF so carrega aqui, no toque -- igual ao PDF do pedido.
  async function gerar(destino: 'compartilhar' | 'baixar') {
    setMenuAberto(false)
    setErroRelatorio(null)
    setGerando(true)
    try {
      const dados = gerarDados()
      // O documento tambem vem sob demanda: importado direto, ele arrastava a
      // biblioteca de PDF inteira para o pacote principal (build recusou: 2,86 MB).
      const [{ pdf }, { DocumentoConferencia, nomeArquivoConferencia }] = await Promise.all([
        import('@react-pdf/renderer'), import('../../comissoes/PdfConferencia'),
      ])
      const blob = await pdf(<DocumentoConferencia dados={dados} />).toBlob()
      const nome = nomeArquivoConferencia(dados.fornecedora, dados.mes)
      if (destino === 'baixar' || (await compartilharArquivo(blob, nome)) === 'indisponivel') {
        baixarArquivo(blob, nome)
      }
    } catch {
      setErroRelatorio('Não deu para gerar o relatório. Tente de novo.')
    } finally {
      setGerando(false)
    }
  }

  return (
    <div
      ref={refDoRodape}
      data-testid="rodape-baixa"
      className="fixed bottom-16 left-0 right-0 bg-white rounded-t-2xl shadow-[0_-4px_12px_rgba(0,0,0,0.08)] p-3 space-y-2"
    >
      {comBaixa && (
        <div className="grid grid-cols-3 gap-2 items-end">
          <div className="min-w-0">
            <p className="text-[11px] text-slate-500">Selecionados ({quantidade})</p>
            <p className="text-sm font-bold text-slate-800 break-words">{formatarMoeda(conta.selecionados)}</p>
          </div>
          <div className="min-w-0 border-l border-slate-200 pl-2">
            <p className="text-[11px] text-slate-500">{rotuloDoAjuste(conta.ajuste)}</p>
            <p className={`text-sm font-bold break-words ${conta.ajuste < 0 ? 'text-red-600' : conta.ajuste > 0 ? 'text-sky-700' : 'text-slate-500'}`}>
              {valorComSinal(conta.ajuste)}
            </p>
          </div>
          <button type="button" onClick={aoAbrirTotal} className="min-w-0 border-l border-slate-200 pl-2 text-right">
            <span className="block text-[11px] text-slate-500">{pagoEDiferenca ? 'Total pago ✎' : 'Total da baixa ✎'}</span>
            {pagoEDiferenca ? (
              <span data-testid="total-pago-rodape" className="block text-base font-extrabold text-slate-900 break-words">{formatarMoeda(pagoEDiferenca.pago)}</span>
            ) : (
              <span data-testid="total-da-baixa" className="block text-base font-extrabold text-slate-900 break-words">{formatarMoeda(conta.total)}</span>
            )}
          </button>
        </div>
      )}
      {comBaixa && pagoEDiferenca && (
        <p data-testid="diferenca-do-mes" className="flex justify-between text-xs">
          <span className="text-slate-500">Calculado <span data-testid="total-da-baixa">{formatarMoeda(conta.total)}</span></span>
          <span className={pagoEDiferenca.diferenca > 0 ? 'text-sky-700 font-semibold' : pagoEDiferenca.diferenca < 0 ? 'text-red-700 font-semibold' : 'text-slate-500'}>
            Diferença do mês {sinal(pagoEDiferenca.diferenca)}{formatarMoeda(Math.abs(pagoEDiferenca.diferenca))}
          </span>
        </p>
      )}
      {comBaixa && foraDoFiltro > 0 && (
        <p data-testid="aviso-fora-do-filtro" className="text-xs text-amber-800 bg-amber-50 rounded-lg px-2 py-1">
          {foraDoFiltro === 1 ? '1 marcada fora do filtro' : `${foraDoFiltro} marcadas fora do filtro`} — entra na baixa
        </p>
      )}
      {erroBaixa && <CartaoErro titulo="Não foi possível confirmar a baixa">{erroBaixa}</CartaoErro>}
      {erroRelatorio && <CartaoErro titulo="Relatório">{erroRelatorio}</CartaoErro>}
      {comBaixa && confirmando ? (
        <div className="space-y-2">
          <p className="text-sm text-slate-700">
            Dar baixa em {quantidade} pedido(s) de {fornecedora} — {pagoEDiferenca
              ? `pago ${formatarMoeda(pagoEDiferenca.pago)} (diferença do mês ${sinal(pagoEDiferenca.diferenca)}${formatarMoeda(Math.abs(pagoEDiferenca.diferenca))})`
              : formatarMoeda(conta.total)}?
          </p>
          <div className="flex gap-2">
            <button
              data-testid="confirmar-baixa"
              onClick={() => void aoDarBaixa()}
              className="flex-1 bg-teal-700 text-white rounded-lg py-2 text-sm font-medium"
            >
              Confirmar
            </button>
            <button onClick={() => setConfirmando(false)} className="flex-1 bg-white border text-slate-600 rounded-lg py-2 text-sm">
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="relative flex gap-2">
          <button
            type="button" data-testid="botao-relatorio" disabled={gerando}
            onClick={() => setMenuAberto((a) => !a)}
            className="flex-1 rounded-xl border-2 border-teal-800 bg-white py-2.5 text-sm font-semibold text-teal-900"
          >
            {gerando ? 'Gerando…' : '📄 Gerar relatório ▾'}
          </button>
          {comBaixa && (
            <button
              data-testid="botao-dar-baixa"
              disabled={quantidade === 0}
              onClick={() => setConfirmando(true)}
              className="flex-1 rounded-xl bg-teal-800 py-2.5 text-sm font-bold text-white ring-2 ring-[#cfa951] disabled:bg-slate-300 disabled:ring-0"
            >
              ✓ Dar baixa
            </button>
          )}
          {menuAberto && (
            <div role="menu" className="absolute bottom-full left-0 mb-2 w-48 rounded-xl bg-white shadow-lg border border-slate-200 overflow-hidden">
              <button role="menuitem" type="button" onClick={() => void gerar('compartilhar')} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                Compartilhar
              </button>
              <button role="menuitem" type="button" onClick={() => void gerar('baixar')} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                Baixar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
